// Foyer's replacement for nenda-autoplayer's src/lib/clients/NendaLogClient.ts.
//
// Upstream POSTs structured log entries to a SEPARATE Nenda log-ingestion API
// (EXPO_PUBLIC_LOG_API_BASE_URL) - a real, external Nenda-owned system this
// repo has no write access to and no business calling regardless (Foyer's
// write boundary is this repo and Foyer's own deploy targets, full stop).
//
// Foyer already has an equivalent-PURPOSE mechanism: `installLogBuffer()`
// (src/tv/lib/logBuffer.ts) patches `console.log/info/warn/error/debug` to
// also record into an in-app ring buffer that a remote `log_capture` command
// can pull on demand - exactly the tool this session used all night to
// diagnose the 2026-09-17/18 incident. Routing the ported machine's own
// heavy internal logging (`log`/`logPlay`/`logPing`/`logContentPlaylistPlay`/
// `logAdView`/`logAdPlaybackError`/`logContentAssetComplete` - all funnel
// through one `sendLogEntry` call each, confirmed by direct grep) through
// plain `console.*` gets it into that SAME pipeline for free, with no new
// transport, no new remote system, and no possibility of it silently
// draining a device's bandwidth to an endpoint nobody here can see.
import { LogLevel, type NendaLogEntry } from "../signage-engine/types/LogEntry";

export interface LogClient {
  sendLogEntry(entry: NendaLogEntry): Promise<void>;
}

const CONSOLE_METHOD: Record<LogLevel, "log" | "warn" | "error"> = {
  [LogLevel.INFO]: "log",
  [LogLevel.WARNING]: "warn",
  [LogLevel.SEVERE]: "error",
};

export class WebLogClient implements LogClient {
  async sendLogEntry(entry: NendaLogEntry): Promise<void> {
    const method = CONSOLE_METHOD[entry.level] ?? "log";
    const parts = [`[signage-engine:${entry.component}]`];
    if (entry.message) parts.push(entry.message);
    if (entry.currentAssetId) {
      parts.push(
        `asset=${entry.currentAssetName ?? entry.currentAssetId} (${entry.currentAssetId})`
      );
    }
    console[method](parts.join(" "));
  }
}
